import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Truck, 
  ShieldCheck, 
  MapPin, 
  Search, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Lock, 
  ExternalLink, 
  Printer, 
  PackageCheck,
  Send,
  FileText
} from 'lucide-react';

export default function AdminShipping() {
  const { orders, addToast } = useShop();

  const [checkPincode, setCheckPincode] = useState('');
  const [pincodeResult, setPincodeResult] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('sequel');

  const carriers = [
    { id: 'sequel', name: 'Sequel 24 SafeLogistics (Armored Transit)', rating: '5.0', type: 'Armored Bullion & Diamonds', maxInsurance: '₹50,00,000' },
    { id: 'bvc', name: 'BVC Logistics (Gems & Jewellery)', rating: '4.9', type: 'High-Value Jewellery Air Freight', maxInsurance: '₹25,00,000' },
    { id: 'bluedart', name: 'Blue Dart Apex (Secured Express)', rating: '4.8', type: 'Surface & Air Express with OTP', maxInsurance: '₹5,00,000' },
    { id: 'delhivery', name: 'Delhivery Surface (Kitchenware)', rating: '4.6', type: 'Heavy Cookware & Dinner Sets', maxInsurance: '₹50,000' }
  ];

  const handleCheckService = (e) => {
    e.preventDefault();
    if (!checkPincode || checkPincode.length !== 6) {
      addToast?.('Please enter a valid 6-digit Indian PIN code', 'error');
      return;
    }
    const isMajorCity = ['110001', '302001', '400001', '560001', '700001', '500001'].some(p => checkPincode.startsWith(p.slice(0, 2)));
    setPincodeResult({
      pincode: checkPincode,
      serviceable: true,
      partner: isMajorCity ? 'Sequel 24 Armored Transit (Next Day Delivery)' : 'Blue Dart Apex Secured (2-3 Days)',
      insuredDelivery: '100% Comprehensive Transit Insurance Covered',
      otpMandatory: true
    });
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <Truck size={14} />
            <span>INSURED HIGH-VALUE LOGISTICS DISPATCH</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Shipping, Armored Transit & Waybills
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            Sequel 24 & BVC Armored Bullion dispatch, tamper-evident security vault seals, PIN code verification and OTP handover.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '0.5rem 0.8rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 800, color: '#065f46' }}>
            <ShieldCheck size={16} />
            <span>100% TRANSIT INSURANCE ACTIVE</span>
          </div>
        </div>
      </div>

      {/* Logistics Partners Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {carriers.map(c => (
          <div
            key={c.id}
            onClick={() => setSelectedPartner(c.id)}
            style={{
              background: '#ffffff',
              borderRadius: '8px',
              padding: '1.2rem',
              border: selectedPartner === c.id ? '2px solid #2874f0' : '1px solid #e0e0e0',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              transition: 'all 0.15s'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#2874f0', letterSpacing: '0.5px' }}>APPROVED PARTNER</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#15803d' }}>★ {c.rating}</span>
            </div>
            <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem', marginBottom: '0.3rem' }}>{c.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.6rem' }}>{c.type}</div>
            <div style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 700, background: '#f8fafc', padding: '0.35rem 0.6rem', borderRadius: '4px' }}>
              Max Coverage: {c.maxInsurance}
            </div>
          </div>
        ))}
      </div>

      {/* Two Column Section: Pincode Checker & Active Dispatches */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(320px, 1.4fr)', gap: '1.5rem' }}>
        
        {/* Pincode Serviceability & Insurance Rules */}
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.3rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <MapPin size={17} color="#2874f0" />
            <span>Pincode Serviceability & OTP Verification</span>
          </h3>

          <form onSubmit={handleCheckService} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="text"
              maxLength={6}
              placeholder="Enter 6-digit Pincode (e.g. 302001)"
              value={checkPincode}
              onChange={(e) => setCheckPincode(e.target.value.replace(/\D/g, ''))}
              style={{ flex: 1, padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
            />
            <button
              type="submit"
              style={{ padding: '0.6rem 1rem', background: '#2874f0', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
            >
              Verify
            </button>
          </form>

          {pincodeResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '1rem', marginBottom: '1.2rem' }}>
              <div style={{ fontWeight: 800, color: '#15803d', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <CheckCircle2 size={16} /> PIN {pincodeResult.pincode}: Direct Armored Route Active
              </div>
              <div style={{ fontSize: '0.78rem', color: '#166534', marginTop: '0.3rem' }}>
                • Recommended Carrier: {pincodeResult.partner}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#166534', marginTop: '0.2rem' }}>
                • Delivery Protocol: OTP Authentication on Handover
              </div>
            </div>
          )}

          {/* Tamper Evident Packing Checklist */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '1rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Vault Packaging Security Protocol:
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.78rem', color: '#475569', lineHeight: '1.6' }}>
              <li>Jewellery placed inside velvet box with silica gel pouch.</li>
              <li>BIS Hallmark card & tax invoice sealed in waterproof pouch.</li>
              <li>Package enclosed in sequential numbered Tamper-Evident Security Bag.</li>
              <li>Consignment handed over to armored courier guard with barcode scan.</li>
            </ul>
          </div>
        </div>

        {/* Live Dispatched Orders */}
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.3rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <PackageCheck size={17} color="#15803d" />
            <span>Active Dispatched Vault Consignments</span>
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {orders.slice(0, 5).map((ord, idx) => (
              <div key={ord.id} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.88rem' }}>Order #{ord.id}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Recipient: {ord.customer?.name || 'Client'} • {ord.customer?.address || 'Jaipur'}</div>
                  <div style={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 700, marginTop: '0.2rem' }}>
                    AWB: SQL-2026-98{idx}42 • Sequel Armored
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.88rem' }}>{formatINR(ord.total || 45000)}</div>
                  <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '3px', background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
                    In Transit (Insured)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
}
